/**
 * Web-Mercator projection, fit-to-bounds and clustering — the whole of the map's
 * arithmetic, with no React and no DOM in it.
 *
 * ── WHY IT IS A SEPARATE FILE ──────────────────────────────────────────────
 * Every property worth proving about a map is arithmetic: that two points a
 * kilometre apart cluster at world zoom and separate at street zoom, that a
 * single point does not zoom to rooftop level, that a reset returns EXACTLY the
 * fit it started from, that a longitude of 181 is refused rather than drawn off
 * the edge of the world. None of that needs a renderer, and proving it through
 * one would mean proving it through jsdom's layout, which has none.
 *
 * ── THE ORIGIN OF THE MATHS ────────────────────────────────────────────────
 * `mercX`/`mercY` and the fit loop were lifted from the console's `SlippyMap`,
 * which is deleted (#2406): nothing imported it, and `MapLens` in this package
 * had already taken over every surface it served. The arithmetic is the part
 * that was worth keeping, and it is here
 * — the console's keyless multi-pin map, which #2267 left orphaned when it
 * retired the CRM record page. It is the existing provider seam and it is
 * KEYLESS, which is the property that matters: raster tiles from a URL template,
 * no API key, nothing to put in a document (`crm/geo.ts` records why both keyed
 * providers were deleted — an embed carries its key in the page, so there is no
 * version of one that is both working and governed).
 *
 * What is NOT carried over is its defining limitation. That component is
 * deliberately read-only — "a situational picture, not a GIS" — and #2276 needs
 * pan, zoom, a stable reset and clustering. So the projection is reused and the
 * viewport is new.
 */

/** One tile's edge, in CSS pixels, at every zoom level. */
export const TILE = 256

/** The zoom a single point settles at — street level, not rooftop. */
export const SOLO_ZOOM = 15
/** The tightest fit a multi-point bbox is allowed to reach. */
export const MAX_FIT_ZOOM = 16
export const MIN_ZOOM = 2
export const MAX_ZOOM = 19

/** Breathing room, in pixels, kept around a fitted bounding box. */
const FIT_PADDING = 90

export interface LatLng { lat: number; lng: number }

/** A point placed in the viewport, in CSS pixels from the canvas origin. */
export interface ScreenPoint { x: number; y: number }

/**
 * The map's whole mutable state: where the centre is and how far in we are.
 * Deliberately a value rather than a class — a reset is then `setView(fit)` and
 * cannot drift from the fit it claims to restore.
 */
export interface MapView extends LatLng { zoom: number }

/** Mercator X in the unit square. `lng` is wrapped, never clamped. */
export const mercX = (lng: number): number => (wrapLng(lng) + 180) / 360

/**
 * Mercator Y in the unit square.
 *
 * Latitude is CLAMPED rather than wrapped, and the two are different for a
 * reason: longitude wraps because the world does, while ±90° is a projection
 * singularity — `tan(π/2)` is infinite, and a point at the pole would place
 * every other pin at NaN. 85.051129° is the standard web-mercator limit.
 */
export const mercY = (lat: number): number => {
  const r = (clampLat(lat) * Math.PI) / 180
  return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2
}

export const MERCATOR_LAT_LIMIT = 85.051129

export const clampLat = (lat: number): number =>
  Math.max(-MERCATOR_LAT_LIMIT, Math.min(MERCATOR_LAT_LIMIT, lat))

/** Longitudes wrap: 181 is -179, and a pin near the date line still draws. */
export const wrapLng = (lng: number): number => {
  const wrapped = ((lng + 180) % 360 + 360) % 360 - 180
  // -180 and 180 are the same meridian; normalise so two equal places produce
  // one cluster key rather than two.
  return wrapped === -180 ? 180 : wrapped
}

/**
 * Coerce a wire value to a number, or null. PostgREST sends a double precision
 * column as TEXT, so a coordinate very often arrives as `'51.2186'`.
 *
 * The empty string is the trap, and it is why this is a function rather than a
 * `Number()` at each call site: **`Number('') === 0`**, and 0 is finite — so a
 * record whose coordinate is blank would be silently PLACED, at the equator, in
 * the Gulf of Guinea, instead of being named in the unavailable list. Same for
 * whitespace, and same for `null` (`Number(null) === 0`). Caught by this file's
 * own test rather than by a reader wondering why one address was in the sea.
 */
export const asCoordinate = (value: unknown): number | null => {
  if (value === null || value === undefined || typeof value === 'boolean') return null
  if (typeof value === 'string' && value.trim() === '') return null
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/**
 * Is this pair drawable at all?
 *
 * The one place the answer is decided, so "missing coordinates" means the same
 * thing in the canvas, in the unavailable list and in the count beside it.
 * `null`, `undefined`, a string the server sent as text, `NaN`, and a latitude
 * outside ±90 are all the SAME answer — and a record that fails it is retained
 * and named rather than dropped, which is the rule this function exists to make
 * enforceable in one place.
 */
export function isPlaceable(lat: unknown, lng: unknown): boolean {
  const a = asCoordinate(lat)
  const b = asCoordinate(lng)
  // 0,0 is in the Gulf of Guinea and is very often a failed geocode rather than
  // a place. It is deliberately still PLACEABLE: filtering it here would hide a
  // legitimate record at Null Island AND make "unplaceable" mean two different
  // things, and this platform's rule is that a row it cannot plot is NAMED, not
  // dropped. Recorded so the next reader does not "fix" it into a filter.
  return a !== null && b !== null && Math.abs(a) <= 90 && Math.abs(b) <= 180
}


/** The world's pixel width at one zoom level. */
export const worldSize = (zoom: number): number => TILE * 2 ** zoom

/** Project a place into viewport pixels for a given view and canvas size. */
export function project(
  place: LatLng, view: MapView, width: number, height: number,
): ScreenPoint {
  const scale = worldSize(view.zoom)
  const originX = mercX(view.lng) * scale - width / 2
  const originY = mercY(view.lat) * scale - height / 2
  return { x: mercX(place.lng) * scale - originX, y: mercY(place.lat) * scale - originY }
}

/** The inverse — a drag needs to know what a pixel offset did to the centre. */
export function unproject(
  point: ScreenPoint, view: MapView, width: number, height: number,
): LatLng {
  const scale = worldSize(view.zoom)
  const originX = mercX(view.lng) * scale - width / 2
  const originY = mercY(view.lat) * scale - height / 2
  const x = (point.x + originX) / scale
  const y = (point.y + originY) / scale
  const lng = x * 360 - 180
  const n = Math.PI - 2 * Math.PI * y
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
  return { lat, lng: wrapLng(lng) }
}

/**
 * The view that shows every place, with room to breathe.
 *
 * Returned as a VALUE so "reset" is literally "use this again" — a reset that
 * recomputed from the current viewport would drift every time somebody pressed
 * it, which is the one thing a reset control must not do.
 *
 * With no places it answers the whole world rather than NaN: an empty map is an
 * honest empty state, and a viewport of NaN is a blank canvas that logs nothing.
 */
export function fitView(places: readonly LatLng[], width: number, height: number): MapView {
  if (places.length === 0) return { lat: 20, lng: 0, zoom: MIN_ZOOM }
  const xs = places.map((p) => mercX(p.lng))
  const ys = places.map((p) => mercY(p.lat))
  const [minX, maxX] = [Math.min(...xs), Math.max(...xs)]
  const [minY, maxY] = [Math.min(...ys), Math.max(...ys)]
  const centre = {
    lat: unmercY((minY + maxY) / 2),
    lng: (minX + maxX) / 2 * 360 - 180,
  }
  if (places.length === 1) return { ...centre, zoom: SOLO_ZOOM }

  // Largest zoom whose bbox still fits the pane with padding. A loop rather than
  // a log: the padding is in PIXELS and the bbox is in world units, so the two
  // only meet at a candidate zoom.
  const usableW = Math.max(1, width - FIT_PADDING)
  const usableH = Math.max(1, height - FIT_PADDING)
  let zoom = MAX_FIT_ZOOM
  while (zoom > MIN_ZOOM) {
    const scale = worldSize(zoom)
    if ((maxX - minX) * scale <= usableW && (maxY - minY) * scale <= usableH) break
    zoom--
  }
  return { ...centre, zoom }
}

const unmercY = (y: number): number => {
  const n = Math.PI - 2 * Math.PI * y
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

export const clampZoom = (zoom: number): number =>
  Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom))

/** Which raster tiles cover the viewport, and where each one sits. */
export interface TileRef { key: string; z: number; x: number; y: number; left: number; top: number }

export function tilesFor(view: MapView, width: number, height: number, maxZoom = MAX_ZOOM): TileRef[] {
  const z = Math.min(Math.round(view.zoom), maxZoom)
  const scale = worldSize(z)
  const originX = mercX(view.lng) * scale - width / 2
  const originY = mercY(view.lat) * scale - height / 2
  const span = 2 ** z
  const tiles: TileRef[] = []
  for (let x = Math.floor(originX / TILE); x <= Math.floor((originX + width) / TILE); x++) {
    for (let y = Math.floor(originY / TILE); y <= Math.floor((originY + height) / TILE); y++) {
      // Above the north pole or below the south is not a tile. Wrapping Y the
      // way X wraps would repeat the map vertically, which no map does.
      if (y < 0 || y >= span) continue
      const wrappedX = ((x % span) + span) % span
      tiles.push({
        key: `${z}/${wrappedX}/${y}/${x}`,
        z, x: wrappedX, y,
        left: x * TILE - originX, top: y * TILE - originY,
      })
    }
  }
  return tiles
}

/** Fill a `{z}/{x}/{y}` template. The host owns the URL; this never invents one. */
export function tileUrl(template: string, tile: TileRef): string {
  return template
    .replace('{z}', String(tile.z))
    .replace('{x}', String(tile.x))
    .replace('{y}', String(tile.y))
}

/* ── clustering ────────────────────────────────────────────────────────────
 *
 * A grid in SCREEN space, not in degrees. Degrees are the obvious choice and the
 * wrong one: a degree of longitude is 111km at the equator and 43km in Antwerp,
 * so a fixed-degree grid clusters aggressively in the north and not at all near
 * the equator — the same two offices would merge in Belgium and separate in
 * Kenya, on the same screen, at the same zoom. A pixel grid asks the question
 * the reader is actually asking: are these two marks on top of each other?
 */
export interface Clusterable extends LatLng { key: string }

export interface Cluster<T extends Clusterable> {
  /** Stable across a re-render at the same view, so React keys do not thrash. */
  key: string
  /** The cluster's own position — the MEAN of its members, not the first one. */
  lat: number
  lng: number
  members: T[]
}

/**
 * Group points that would overlap at this view into one mark.
 *
 * `cellPx` is the grid pitch: two points in the same cell become one cluster.
 * 56px is a little over one touch target, which is the honest threshold — below
 * it the two marks cannot both be hit anyway.
 */
export function clusterPoints<T extends Clusterable>(
  points: readonly T[], view: MapView, width: number, height: number, cellPx = 56,
): Cluster<T>[] {
  const scale = worldSize(view.zoom)
  const buckets = new Map<string, T[]>()
  for (const point of points) {
    const px = mercX(point.lng) * scale
    const py = mercY(point.lat) * scale
    const cell = `${Math.floor(px / cellPx)}:${Math.floor(py / cellPx)}`
    const bucket = buckets.get(cell)
    if (bucket) bucket.push(point); else buckets.set(cell, [point])
  }
  const clusters: Cluster<T>[] = []
  for (const members of buckets.values()) {
    // The MEAN, so a cluster mark sits among its members rather than on one of
    // them — a cluster drawn at its first member's exact spot hides that member
    // behind its own count badge.
    const lat = members.reduce((sum, m) => sum + clampLat(m.lat), 0) / members.length
    const lng = meanLng(members)
    clusters.push({
      // Keyed on the MEMBERS, not on the cell: the cell moves with the view, so
      // a cell key would remount every mark on every pan and take the open
      // preview card with it. Sorted, so a re-order upstream is not a new mark.
      key: members.map((m) => m.key).sort().join('|'),
      lat, lng, members,
    })
  }
  // Stable order, so the DOM order a screen reader walks does not shuffle when
  // a pan changes nothing else. North to south, then west to east — the order a
  // European reader scans a map in.
  return clusters.sort((a, b) => b.lat - a.lat || a.lng - b.lng || a.key.localeCompare(b.key))
}

/**
 * The mean of a cluster's longitudes.
 *
 * ── THIS USED TO AVERAGE ANGLES, AND THAT WAS UNREACHABLE ──────────────────
 * It averaged unit vectors and took the angle back, with a comment explaining
 * that a plain average is wrong across the date line — 179 and -179 average to
 * 0, which is the Gulf of Guinea. The reasoning is correct and the situation
 * cannot arise: the cluster grid is WORLD-pixel space, where 179.9° and -179.9°
 * sit at opposite edges of the world (mercX 0.9997 and 0.0003), roughly a whole
 * world apart. **Two places either side of the antimeridian are never in the
 * same cell, so a cluster never spans it, so the mean never has to.**
 *
 * Found by a mutation arm that would not go red: replacing the angle mean with
 * a plain average changed nothing any test could see, because the fixture meant
 * to exercise it produced TWO clusters and the assertion read the first one.
 *
 * So the angle mean is gone rather than kept with a comment claiming it
 * prevents a defect that cannot happen. The limitation is asserted instead —
 * see "two places either side of the antimeridian are two marks" — because a
 * limitation nobody wrote down is the one somebody later calls a bug.
 */
function meanLng(points: readonly LatLng[]): number {
  return points.reduce((sum, p) => sum + wrapLng(p.lng), 0) / points.length
}
